import React from 'react'

// Shared presentation layer for the Test / Homework "past" & "upcoming"
// card grids (studentTestComp.js and studentHomeworkComp.js). Kept in one
// place so both surfaces stay visually identical and only need one edit.

export const STUDY_CARD_STYLES = `
 .study-card-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 1.1rem; align-items: stretch; }

 .study-section-header { display: flex; align-items: center; gap: .65rem; }
 .study-section-icon { width: 38px; height: 38px; border-radius: .8rem; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
 .study-section-icon .material-symbols-outlined { font-size: 20px; }
 .study-count-chip { display: inline-flex; align-items: center; justify-content: center; min-width: 30px; height: 28px; padding: 0 .55rem; border-radius: 999px; font-weight: 700; font-size: .82rem; }

 .study-card {
   position: relative;
   border-radius: 1rem;
   background: var(--bs-body-bg);
   border: 1px solid var(--bs-border-color-translucent, rgba(0,0,0,.07));
   box-shadow: 0 1px 2px rgba(0,0,0,.05);
   overflow: hidden;
   transition: box-shadow .2s ease, transform .2s ease, border-color .2s ease;
 }
 .study-card::before { content: ''; position: absolute; inset: 0 auto 0 0; width: 4px; }
 .study-card-upcoming::before { background: linear-gradient(180deg, var(--bs-primary), rgba(var(--bs-primary-rgb), .35)); }
 .study-card-past::before { background: linear-gradient(180deg, var(--bs-secondary), rgba(var(--bs-secondary-rgb), .3)); }
 .study-card:hover { transform: translateY(-3px); box-shadow: 0 .75rem 1.75rem rgba(var(--bs-primary-rgb), .14); border-color: rgba(var(--bs-primary-rgb), .25); }
 .study-card-today { border-color: rgba(var(--bs-success-rgb), .4); box-shadow: 0 0 0 2px rgba(var(--bs-success-rgb), .18), 0 .5rem 1.25rem rgba(var(--bs-success-rgb), .12); }
 .study-card-body { padding: 1.1rem 1.15rem 1rem 1.3rem; }

 .study-card-icon { width: 42px; height: 42px; border-radius: .8rem; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
 .study-card-icon .material-symbols-outlined { font-size: 21px; }
 .study-card-eyebrow { font-size: .66rem; font-weight: 700; letter-spacing: .07em; text-transform: uppercase; line-height: 1.4; }
 .study-card-title { font-size: .98rem; font-weight: 600; line-height: 1.3; }

 .study-card-meta { display: flex; flex-wrap: wrap; row-gap: .3rem; column-gap: .9rem; font-size: .78rem; color: var(--bs-gray-600); margin-bottom: .7rem; }
 .study-card-meta .material-symbols-outlined { font-size: 15px; vertical-align: -3px; margin-right: 3px; }

 .study-status-pill { display: inline-flex; align-items: center; gap: 4px; font-size: .72rem; font-weight: 700; padding: .32rem .7rem; border-radius: 999px; white-space: nowrap; }
 .study-status-pill .material-symbols-outlined { font-size: 14px; }

 .study-card-score { display: flex; align-items: center; gap: 4px; font-size: .84rem; }
 .study-card-score .material-symbols-outlined { font-size: 16px; color: var(--bs-warning); }

 .study-cta-start { background: linear-gradient(135deg, var(--bs-success), rgba(var(--bs-success-rgb), .82)); border: none; box-shadow: 0 .35rem 1rem rgba(var(--bs-success-rgb), .3); }
 .study-cta-start:hover { filter: brightness(1.08); }

 .study-empty { grid-column: 1 / -1; text-align: center; padding: 2.5rem 1rem 2rem; }
 .study-empty-icon { width: 64px; height: 64px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto .9rem; }
 .study-empty-icon .material-symbols-outlined { font-size: 30px; }
 .study-empty-title { font-weight: 600; margin-bottom: .25rem; }
 .study-empty-subtitle { font-size: .82rem; color: var(--bs-gray-600); margin: 0; }

 .exam-nav-dot {
   width: 34px; height: 34px; padding: 0; border-radius: 50%;
   border: 1px solid var(--bs-border-color); background: var(--bs-body-bg); color: var(--bs-body-color);
   font-size: .8rem; font-weight: 700; display: inline-flex; align-items: center; justify-content: center;
   transition: all .15s ease;
 }
 .exam-nav-dot:disabled { opacity: .55; cursor: not-allowed; }
 .exam-nav-dot-answered { background: var(--bs-success); border-color: var(--bs-success); color: #fff; }
 .exam-nav-dot-current { box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), .35); border-color: var(--bs-primary); }
 .exam-option {
   display: flex; align-items: center; padding: .65rem .9rem; margin-bottom: .5rem;
   border: 1px solid var(--bs-border-color); border-radius: .6rem; transition: border-color .15s ease;
 }
 .exam-option:hover { border-color: var(--bs-primary); }
 .exam-option-selected { border-color: var(--bs-primary); }
 .exam-timer-pulse { animation: examTimerPulse 1s ease-in-out infinite; }
 @keyframes examTimerPulse { 0%, 100% { opacity: 1; } 50% { opacity: .5; } }
`;

// Compact section header used in each Card.Header (icon + title + count chip).
export const StudySectionHeader = ({ icon, title, tone = 'primary', count }) => (
    <>
        <div className="study-section-header">
            <div className={`study-section-icon bg-soft-${tone} text-${tone}`}>
                <i className="material-symbols-outlined">{icon}</i>
            </div>
            <div className="header-title">
                <h4 className="card-title mb-0">{title}</h4>
            </div>
        </div>
        <span className={`study-count-chip bg-soft-${tone} text-${tone}`}>{count}</span>
    </>
);

// Compact, in-grid empty state — replaces the oversized generic
// NoDataFound illustration inside these two-column card grids.
export const StudyEmptyState = ({ icon = 'inbox', tone = 'secondary', title, subtitle }) => (
    <div className="study-empty">
        <div className={`study-empty-icon bg-soft-${tone} text-${tone}`}>
            <i className="material-symbols-outlined">{icon}</i>
        </div>
        <div className="study-empty-title">{title}</div>
        {subtitle && <p className="study-empty-subtitle">{subtitle}</p>}
    </div>
);

// Normalizes a test/homework answer field for rendering. MCQ questions come
// back as an array of { id, option_text } objects; SAQ/LAQ (free-text)
// questions come back as a plain string (or are simply empty/absent). This
// gives both shapes a single { id, text } form so callers can always .map()
// over the result instead of assuming an array.
export const toAnswerChips = (value) => {
    if (Array.isArray(value)) {
        return value.map((v, i) => ({ id: v?.id ?? i, text: v?.option_text ?? '' }));
    }
    if (value === null || value === undefined || value === '') return [];
    return [{ id: 'single', text: String(value) }];
};
